import {useEffect, useMemo, useState} from 'react';
import cn from 'classnames';
import {BottomSheet} from 'react-spring-bottom-sheet';
import Accordion from '../../../../components/mazraeKit/accordion';
import CustomMultiCheckbox from '../../../../components/mazraeKit/customMultiCheckbox';
import MultiRangeSlider from '../../../../components/multiRangeSlider';
import {IFiltersData, filtersData, multiSelectData} from './utils';
import useGetRange from '../../../../services/hookService/getRange/useGetRange';
import DotsLoading from '../../../../components/dotsLoading';
import {useLocation, useParams} from 'react-router-dom';
import {useGetInputGroupList} from '../../../../services/hookService';
import CategoryCards from '../../../../module/newShop/home/inputGroups';
import {useAdvertisementFilters} from '../../../../hooks/filters/useAdvertisementFilter';
import type {SelectedInputGroup} from '../../../../hooks/filters/types/filters.type';
import CardInputGroup from '../../../../components/mazraeKit/CardInputGroup';
import {
  getNameByInputGroup,
  getInputGroupImage,
  getInputGroupCode,
} from '../../../../module/dashboard/category/utilities';
import useGetInputLists from '../../../../services/hookService/getInputLists/useGetInputLists';
import SubCategoryCards from '../../../../module/newShop/home/subInputGroups';

export interface IFilters {
  categories: Array<SelectedInputGroup>;
  sorting: string;
  has_price: boolean;
  has_store: boolean;
  has_immediate: boolean;
  has_special_offer: boolean;
  sub_categories: Array<SelectedInputGroup>;
}

const InputPostListBottomSheet = ({
  openBottomSheet,
  setOpenBottomSheet,
  code,
  title,
  isShowType,
  isCategoriesPage = false,
}: {
  openBottomSheet: boolean;
  setOpenBottomSheet: React.Dispatch<React.SetStateAction<boolean>>;
  code?: number;
  title?: string;
  isShowType?: boolean;
  isCategoriesPage?: boolean;
}) => {
  const params = useParams();
  const subInputGroupName = params?.subInputGroupName ?? '';
  const {inputGroupName} = params;
  const inputGroupCode = getInputGroupCode[inputGroupName ?? ''];
  const {inputGroupList} = useGetInputGroupList();
  const {data: priceRangeData, isLoading} = useGetRange(
    inputGroupList?.find(item => item?.code === code)?.guid,
  );
  const {data: inputList} = useGetInputLists({
    enabled: !!title,
    input_group__code: code,
  });

  const location = useLocation();
  const isEmployeeAds = location.pathname.split('/').includes('employee');

  const {
    filters: advertisementFilter,
    setFilters: setAdvertisementFilter,
    reset: resetFilters,
  } = useAdvertisementFilters();

  const [minValue, setMinValue] = useState<number>(
    advertisementFilter?.start_price ?? 0,
  );
  const [maxValue, setMaxValue] = useState<number>(
    advertisementFilter?.end_price ?? 9999999,
  );
  const [openAccordion, setOpenAccordion] = useState<boolean>(false);

  const calculateMaxRange = useMemo((): number => {
    if (isEmployeeAds) return 10000000;
    return priceRangeData?.max || 9999999;
  }, [priceRangeData, isEmployeeAds]);

  const calculateMinRange = useMemo((): number => {
    if (isEmployeeAds) return 0;
    return priceRangeData?.min || 0;
  }, [priceRangeData]);

  const buildDraftFromSaved = (): IFilters => ({
    categories: advertisementFilter.input_group_guid,
    sorting: advertisementFilter.order_by || '',
    has_price: advertisementFilter.has_price,
    has_store: advertisementFilter.has_store,
    has_immediate: advertisementFilter.has_immediate,
    has_special_offer: advertisementFilter.has_special_offer,
    sub_categories: advertisementFilter.input_guid,
  });

  const [filters, setFilters] = useState<IFilters>(buildDraftFromSaved);

  const sortingHandler = (sortingItem: IFiltersData) => {
    setFilters(prev => ({...prev, sorting: sortingItem.value}));
  };

  const checkBoxHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({...prev, [event.target.name]: event.target.checked}));
  };

  useEffect(() => {
    setFilters(buildDraftFromSaved());
  }, [priceRangeData]);

  useEffect(() => {
    if (advertisementFilter.start_price === undefined)
      setMinValue(calculateMinRange);
    if (advertisementFilter.end_price === undefined)
      setMaxValue(calculateMaxRange);
  }, [calculateMaxRange, calculateMinRange]);

  const handleDismiss = () => {
    setOpenBottomSheet(false);
    setFilters(buildDraftFromSaved());
  };

  const handleApply = () => {
    const isPriceFilterChanged =
      minValue !== calculateMinRange || maxValue !== calculateMaxRange;
    const filtersData = {
      input_group_guid: filters.categories,
      input_guid: filters.sub_categories,
      order_by: filters.sorting,
      has_store: filters.has_store,
      has_price: filters.has_price,
      has_immediate: filters.has_immediate,
      has_special_offer: filters.has_special_offer,
      ...(isPriceFilterChanged && {
        start_price: minValue,
        end_price: maxValue,
      }),
    };

    setAdvertisementFilter(filtersData);
    setOpenBottomSheet(false);
  };

  const handleClear = () => {
    resetFilters();
    setFilters({
      categories: [],
      sorting: '',
      has_price: false,
      has_store: false,
      has_immediate: false,
      has_special_offer: false,
      sub_categories: [],
    });
    setMinValue(calculateMinRange);
    setMaxValue(calculateMaxRange);
    setOpenBottomSheet(false);
  };

  useEffect(() => {
    setFilters({
      categories: advertisementFilter?.input_group_guid,
      has_price: advertisementFilter.has_price,
      has_immediate: advertisementFilter.has_immediate,
      has_store: advertisementFilter.has_store,
      has_special_offer: advertisementFilter.has_special_offer,
      sorting: advertisementFilter.order_by,
      sub_categories: advertisementFilter.input_guid,
    });
    advertisementFilter?.end_price
      ? setMaxValue(advertisementFilter.end_price)
      : setMaxValue(calculateMaxRange);
    advertisementFilter?.start_price
      ? setMinValue(advertisementFilter.start_price)
      : setMinValue(calculateMinRange);
  }, [openBottomSheet, advertisementFilter]);

  return (
    <>
      <BottomSheet
        onDismiss={handleDismiss}
        open={openBottomSheet}
        blocking={true}
        snapPoints={({minHeight, maxHeight}) => [minHeight, maxHeight / 0.8]}
        className="transition ease-in-out delay-250"
      >
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <DotsLoading />
          </div>
        ) : (
          <div className="pt-2 pb-3 max-h-[80dvh] overflow-y-auto relative">
            <div className="flex pt-2 pb-3 bg-white justify-between border-b px-3 sticky top-[-10px]">
              <p className="font-Medium">فیلتر</p>
              <i
                className="text-xl icon-nameClose-remove"
                onClick={() => setOpenBottomSheet(false)}
              />
            </div>
            {isShowType && subInputGroupName && (
              <div className="flex gap-2 items-center px-3 mt-3 mb-4 w-full">
                <span className="text-sm">نوع {title}:</span>
                <span className="p-1 min-w-12 text-sm text-center bg-lime-50 rounded-lg border-[1px] border-farm-shop-400">
                  {subInputGroupName}
                </span>
              </div>
            )}
            {inputGroupName && (
              <>
                <div className="px-3 mt-3 mb-4">
                  <span className="text-sm">دسته بندی</span>
                </div>
                <div className="px-4 w-full">
                  <CardInputGroup
                    isActive={true}
                    isBoled={false}
                    title={getNameByInputGroup[inputGroupName]}
                    image={getInputGroupImage[inputGroupCode]}
                  />
                </div>
              </>
            )}
            {isCategoriesPage && inputList && !subInputGroupName && (
              <>
                <div className="px-3 mt-3 mb-4">
                  <span className="text-sm">زیر دسته</span>
                </div>
                <div className="flex justify-center w-full">
                  <SubCategoryCards
                    filters={filters}
                    setFilters={setFilters}
                    subInputGroupList={inputList}
                    isSubCategory={true}
                  />
                </div>
              </>
            )}
            {!inputGroupName && (
              <CategoryCards filters={filters} setFilters={setFilters} />
            )}
            <div className="flex items-center px-3 border-b">
              <Accordion
                accordionTitle="مرتب‌سازی بر اساس"
                setOpenAccordion={setOpenAccordion}
                openAccordion={openAccordion}
              >
                <div className="flex overflow-x-auto gap-2 pb-3">
                  {filtersData(inputGroupName as string).map(item => {
                    return (
                      <div
                        key={item.id}
                        className={cn(
                          'border-[1px] text-sm text-farm-shop-500 rounded-full border-farm-shop-500 w-fit py-2 px-2 whitespace-nowrap',
                          {
                            'bg-farm-shop-100': item.value === filters.sorting,
                          },
                        )}
                        onClick={() => sortingHandler(item)}
                      >
                        {item.title}
                      </div>
                    );
                  })}
                </div>
              </Accordion>
            </div>
            <div className="flex flex-col gap-3 px-3 my-4">
              {multiSelectData(inputGroupName as string).map(item => {
                return (
                  <CustomMultiCheckbox
                    key={`${item.title}-${item.id}`}
                    title={item.title}
                    id={item.id}
                    value={item.value}
                    name={item.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      checkBoxHandler(e)
                    }
                    defaultChecked={
                      advertisementFilter[
                        item.value as keyof typeof advertisementFilter
                      ]
                        ? true
                        : false
                    }
                    checked={
                      filters[item.value as keyof typeof filters] as boolean
                    }
                  />
                );
              })}
            </div>

            <div className="flex flex-col px-3 text-sm text-gray-950">
              <span> محدوده قیمت (تومان) </span>
              <div className="relative mt-4">
                <div className="d-flex justify-content-between">
                  <small>از{Number(minValue).toLocaleString()}</small>
                  <small>تا{Number(maxValue).toLocaleString()}</small>
                </div>
                {(priceRangeData || !isShowType) && (
                  <MultiRangeSlider
                    wrapperClass=" mt-2 mb-3"
                    min={calculateMinRange}
                    max={calculateMaxRange}
                    minVal={minValue}
                    maxVal={maxValue}
                    setMaxVal={setMaxValue}
                    setMinVal={setMinValue}
                  />
                )}
              </div>
            </div>
            <div className="sticky bottom-[-20px] z-10 py-2 bg-white border-t border-gray-200 ">
              <div className="flex flex-1 gap-2 px-3">
                <button
                  className="flex justify-center items-center w-3/5 rounded text-white py-2.5 bg-farm-shop-500"
                  onClick={handleApply}
                >
                  اعمال فیلترها
                </button>
                <button
                  className="flex justify-center items-center w-2/5 rounded py-2.5 border-[1px] text-red-500 !border-red-500"
                  onClick={handleClear}
                >
                  حذف فیلترها
                </button>
              </div>
            </div>
          </div>
        )}
      </BottomSheet>
    </>
  );
};

export default InputPostListBottomSheet;
